import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UploadService } from '../../services/upload.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LanguageService } from '../../services/language.service';
import { Language } from '../../models/language.model';
import { LucideAngularModule } from 'lucide-angular';
import { BehaviorSubject, forkJoin, Observable, Subscription, take } from 'rxjs';
import { languagesMustDifferValidator } from '../../validators/languages-must-different';
import { AsyncPipe, CommonModule, NgStyle } from '@angular/common';
import { Store } from '@ngrx/store';
import { CreateJobResponse } from '../../models/create-job-response.model';
import { Job } from '../../class/job.class';
import { JobModel } from '../../models/job.model';
import { JobsActions } from '../../store/jobs/jobs.actions';
import { ResponsiveService } from '../../services/reponsive.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { selectLanguageById } from '../../store/languages/laguages.selects';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../boutons/button/button.component';
import { ClientActions } from '../../store/client/client.actions';

@Component({
  selector: 'app-upload',
  imports: [ReactiveFormsModule, ButtonComponent, LottieComponent, TranslateModule, LucideAngularModule, CommonModule, AsyncPipe, NgStyle],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss'
})
export class UploadComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer') videoPlayerRef!: ElementRef<HTMLVideoElement>;

  isDesktop$: Observable<boolean> = this.responsiveService.desktop$;
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  options: AnimationOptions = {
    path: '/assets/animations/svet-loader.json',
  };
  subscriptions: Subscription[] = [];
  uploadForm: FormGroup = new FormGroup({
    upload: new FormControl(null, Validators.required),
    title: new FormControl<string | null>(null, Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(40)])),
    context: new FormControl<string | null>(null),
    languageTo: new FormControl<number | null>(null, [Validators.required]),
    languageFrom: new FormControl<number | null>(null, [Validators.required]),
  }, { validators: [languagesMustDifferValidator] });

  languages: Language[] = [];
  defaultOriginalLanguageId: number | undefined;
  defaultOutputLanguageId: number | undefined;
  uploadResponse: any = undefined;
  uploadVideoFormat: string = '';
  previewUrl: string | null = null;
  fileSize: number | null = null;
  @Output() complete: EventEmitter<number | null> = new EventEmitter<number | null>();
  videoDuration: number = 0; // durée de la video en secondes

  constructor(
    private uploadService: UploadService,
    private languageService: LanguageService,
    private store: Store,
    private responsiveService: ResponsiveService
  ) { }

  onFileSelected(event: Event) {
    this.isLoading$.next(true);
    const input: HTMLInputElement = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.fileSize = null;
      };

      const file = input.files[0];
      this.uploadForm.get('upload')?.setValue(file);
      this.uploadVideoFormat = file.type;
      this.previewUrl = URL.createObjectURL(file);
      this.fileSize = file.size / Math.pow(1024, 3);

      if (this.uploadForm.controls['title'].value === null) {
        this.uploadForm.controls['title'].setValue(file.name);
      }

      setTimeout(() => {
        this.isLoading$.next(false);

        // Attendre que la vidéo soit bien présente dans le DOM
        requestAnimationFrame(() => {
          const videoElement = this.videoPlayerRef?.nativeElement;
          if (!videoElement) {
            console.error('Élément vidéo non trouvé');
            return;
          }

          videoElement.load();

          videoElement.onloadedmetadata = () => {
            this.videoDuration = Math.floor(videoElement.duration);
          };
        });
      });
    }
  }

  onSubmit() {
    this.isLoading$.next(true);
    const file: File = this.uploadForm.get('upload')?.value;
    if (!file) {
      console.error('Aucun fichier sélectionné');
      this.complete.next(null);
      this.isLoading$.next(false);
      return;
    }
    this.subscriptions.push(
      this.uploadService.uploadVideo(file).subscribe({
        next: (uploadResponse: any) => {
          if (uploadResponse.body) {
            const body: any = uploadResponse.body;
            this.subscriptions.push(
              this.uploadService.sendData(
                body.id,
                this.uploadForm.controls['title'].value,
                this.uploadForm.controls['context'].value,
                this.videoDuration,
                this.uploadForm.controls['languageFrom'].value,
                this.uploadForm.controls['languageTo'].value)
                .subscribe({
                  next: (dataResponse: CreateJobResponse) => {
                    const safeDate = (value: any): Date => {
                      const date = new Date(value);
                      return isNaN(date.getTime()) ? new Date() : date;
                    };
                    const newJobModel: JobModel = {
                      id: dataResponse.job.id,
                      user: dataResponse.job.user,
                      title: dataResponse.job.title,
                      context: dataResponse.job.context,
                      file_extension: dataResponse.job.file_extension,
                      status: dataResponse.job.status,
                      duration: this.videoDuration,
                      thumbnailUrl: dataResponse.job.thumbnailUrl,
                      lang_source: {
                        id: dataResponse.job.lang_source,
                        name: '',
                        short: ''
                      },
                      lang_cible: {
                        id: dataResponse.job.lang_cible,
                        name: '',
                        short: ''
                      },
                      tasks: [...dataResponse.tasks],
                      created_at: safeDate(dataResponse.job.created_at),
                      updated_at: safeDate(dataResponse.job.updated_at),
                      deleted_at: safeDate(dataResponse.job.deleted_at),
                    };
                    forkJoin([
                      this.store.select(selectLanguageById(dataResponse.job.lang_source)).pipe(take(1)),
                      this.store.select(selectLanguageById(dataResponse.job.lang_cible)).pipe(take(1))
                    ] as [Observable<Language | undefined>, Observable<Language | undefined>]).subscribe({
                      next: ([lang_source, lang_cible]: [Language | undefined, Language | undefined]) => {
                        const newJob: Job = new Job({
                          ...newJobModel,
                          lang_cible: lang_cible || { id: dataResponse.job.lang_source, name: '', short: '' },
                          lang_source: lang_source || { id: dataResponse.job.lang_source, name: '', short: '' },
                        });
                        this.store.dispatch(JobsActions.addJob({ job: newJob }))
                        this.store.dispatch(ClientActions.setTokens({ tokens: dataResponse.token_left }))
                        this.complete.next(dataResponse.job.id);
                        this.isLoading$.next(false);
                      }
                    });
                  },
                  error: (error: Error) => {
                    console.error(error)
                    this.complete.next(null);
                    this.isLoading$.next(false);
                  },
                  complete: () => {
                    this.uploadResponse = undefined;
                    this.isLoading$.next(false);
                  },
                }))
          }
        },
        error: (error) => {
          console.error('Échec de l\'upload', error);
          this.isLoading$.next(false);
          this.complete.next(null);
        },
      }));
  }

  removeVideo() {
    this.uploadForm.get('upload')?.setValue(null);
    this.previewUrl && URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
    this.fileSize = null;
  }

  ngOnInit(): void {
    const languageSub = this.languageService.getLanguages().subscribe({
      next: (languages: Language[]) => {
        this.languages = languages;
        this.defaultOriginalLanguageId = languages.find(lang => lang.short === 'fr')?.id || 1;
        this.defaultOutputLanguageId = languages.find(lang => lang.short === 'en')?.id || 2;
        this.uploadForm.patchValue({
          languageFrom: this.defaultOriginalLanguageId,
          languageTo: this.defaultOutputLanguageId
        });
      },
      error: (err: Error) => {
        console.error('Erreur lors de la récupération des langues :', err);
      }
    });
    this.subscriptions.push(languageSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
